// filter-builder.service.ts
import { Injectable } from '@angular/core';
import { NewReportingFilter, SelectionOutput, ReportConfig } from '../interfaces/new-reporting-filter';

@Injectable({
  providedIn: 'root'
})
export class FilterBuilderService {

  /**
   * Baut Filter basierend auf Report-Config
   * - Required Fields: Immer inkl.
   * - Optional Fields: Nur wenn nicht null/undefined
   * - Additional Fields: Merged rein
   */
  buildFilterForReport(
    baseFilter: NewReportingFilter,
    config: ReportConfig,
    additionalData?: Partial<NewReportingFilter>
  ): NewReportingFilter {
    // Starte mit einem leeren Filter
    const reportFilter: Partial<NewReportingFilter> = {};

    // 1. Required Fields - MÜSSEN vorhanden sein
    config.requiredFields.forEach(field => {
      const value = baseFilter[field];
      if (value !== undefined && value !== null) {
        // Type-safe Assignment
        (reportFilter as any)[field] = value;
      }
    });

    // 2. Optional Fields - Nur wenn vorhanden
    config.optionalFields?.forEach(field => {
      const value = baseFilter[field];
      if (value !== undefined && value !== null) {
        (reportFilter as any)[field] = value;
      }
    });

    // 3. Additional Fields - Hardcoded (z.B. minutes: 720)
    if (config.additionalFields) {
      Object.assign(reportFilter, config.additionalFields);
    }

    // 4. Extra Daten mergen (z.B. deChannels vom UI)
    if (additionalData) {
      Object.assign(reportFilter, additionalData);
    }

    return reportFilter as NewReportingFilter;
  }

  /**
   * Validiert ob Filter complete ist
   */
  validateFilterForReport(
    filter: NewReportingFilter,
    config: ReportConfig
  ): { valid: boolean; missingFields: string[] } {
    const missing: string[] = [];

    config.requiredFields.forEach(field => {
      const value = filter[field as keyof NewReportingFilter];

      // Spezial-Check für Arrays (z.B. deChannels)
      if (Array.isArray(value)) {
        if (value.length === 0) {
          missing.push(field);
        }
      } else if (value === undefined || value === null || value === '') {
        missing.push(field);
      }
    });

    return {
      valid: missing.length === 0,
      missingFields: missing
    };
  }

  /**
   * Baut Filter aus UI-State (Selection + DateRange)
   */
  buildFilterFromSelection(
    selectionOutput: SelectionOutput,
    startDate: Date,
    endDate: Date
  ): NewReportingFilter {
    if (!selectionOutput) {
      throw new Error('Selection output required');
    }

    var filter: Partial<NewReportingFilter> = {
      utcFrom: startDate.toISOString()
    };

    var nowTime = new Date();
    var theEndDateFullyIncluded = new Date(endDate);
    theEndDateFullyIncluded.setDate(theEndDateFullyIncluded.getDate() + 1);
    theEndDateFullyIncluded.setMilliseconds(theEndDateFullyIncluded.getMilliseconds() - 1);

    if (theEndDateFullyIncluded.getTime() <  nowTime.getTime())
      filter.utcTo =  theEndDateFullyIncluded.toISOString()
    else
      filter.utcTo = nowTime.toISOString()
    
    // Basierend auf Selection Type
    switch (selectionOutput.type) {
      case 'Projekte':
        filter.projectId = selectionOutput.id;
        break;
      case 'group':
        filter.groupId = selectionOutput.id;
        break;
      case 'RouteStations': // SAM
        filter.samId = selectionOutput.id;
        break;
    }

    return filter as NewReportingFilter;
  }

  /**
   * Cleaner: Merge mehrere Filter
   */
  mergeFilters(...filters: Partial<NewReportingFilter>[]): NewReportingFilter {
    const merged: Partial<NewReportingFilter> = {};

    filters.forEach(filter => {
      Object.assign(merged, filter);
    });

    return merged as NewReportingFilter;
  }

  /**
   * Entfernt undefined/null Values
   */
  cleanFilter(filter: NewReportingFilter): NewReportingFilter {
    const cleaned: Partial<NewReportingFilter> = {};

    Object.keys(filter).forEach(key => {
      const value = filter[key as keyof NewReportingFilter];

      // Nur nicht-null/undefined Values
      if (value !== undefined && value !== null) {
        (cleaned as any)[key] = value;
      }
    });

    return cleaned as NewReportingFilter;
  }
}
